/*
  Cart recommendations
  - <cart-recommendations>: fetches Shopify's related products for each cart item,
    adds collection fallbacks, mixes them, removes duplicates / cart items, and shows them.
  - Cart drawer: loads the "cart-recommendations" section into [data-cart-drawer-recs]
    whenever the drawer is open and the block hasn't been filled yet (e.g. after the
    theme re-renders the drawer when the cart changes).
*/

if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        if (this._loaded) return;
        this._loaded = true;
        this.load();
      }

      async load() {
        const sourceIds = (this.dataset.sourceIds || '').split(',').filter(Boolean);
        const cartIds = new Set((this.dataset.cartIds || '').split(',').filter(Boolean));
        const limit = parseInt(this.dataset.limit, 10) || 4;
        const wrapper = this.closest('.cart-recs');
        const grid = this.querySelector('[data-cart-recs-grid]');
        if (!sourceIds.length || !grid) return;

        // Per cart product: Shopify's related products first, then its collection fallback
        const lists = await Promise.all(
          sourceIds.map(async (id) => {
            const fromShopify = await this.fetchFor(id).catch(() => []);
            const tpl = this.querySelector('template[data-cart-recs-fallback="' + id + '"]');
            const fromCollection = tpl ? Array.from(tpl.content.querySelectorAll('.cart-recs__item')) : [];
            return fromShopify.concat(fromCollection);
          })
        );

        // Take turns between cart products so every item gets a say
        const picked = [];
        const seen = new Set();
        const longest = Math.max(0, ...lists.map((l) => l.length));
        for (let i = 0; i < longest && picked.length < limit; i++) {
          for (const list of lists) {
            const el = list[i];
            if (!el) continue;
            const id = el.dataset.productId;
            if (seen.has(id) || cartIds.has(id)) continue;
            seen.add(id);
            picked.push(el);
            if (picked.length >= limit) break;
          }
        }

        if (!picked.length) {
          if (wrapper) wrapper.hidden = true;
          return;
        }

        grid.replaceChildren(...picked.map((el) => document.importNode(el, true)));
        if (wrapper) wrapper.hidden = false;
        this.setupSlider(grid);
        this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
      }

      // Arrows + click-and-drag for mouse users (touch screens already swipe)
      setupSlider(grid) {
        const nav = this.querySelector('[data-cart-recs-nav]');
        const prev = this.querySelector('[data-cart-recs-prev]');
        const next = this.querySelector('[data-cart-recs-next]');

        const step = () => {
          const item = grid.querySelector('.cart-recs__item');
          const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
          return item ? item.getBoundingClientRect().width + gap : grid.clientWidth * 0.8;
        };

        const update = () => {
          const scrollable = grid.scrollWidth > grid.clientWidth + 2;
          grid.classList.toggle('is-scrollable', scrollable);
          if (nav) nav.hidden = !scrollable;
          if (prev) prev.disabled = grid.scrollLeft <= 2;
          if (next) next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
        };

        if (prev) prev.addEventListener('click', () => grid.scrollBy({ left: -step(), behavior: 'smooth' }));
        if (next) next.addEventListener('click', () => grid.scrollBy({ left: step(), behavior: 'smooth' }));
        grid.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        if ('ResizeObserver' in window) new ResizeObserver(update).observe(grid);

        // Mouse drag
        let startX = 0;
        let startScroll = 0;
        let isDown = false;
        let moved = false;

        grid.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0 || !grid.classList.contains('is-scrollable')) return;
          isDown = true;
          moved = false;
          startX = e.clientX;
          startScroll = grid.scrollLeft;
        });

        window.addEventListener('pointermove', (e) => {
          if (!isDown) return;
          const dx = e.clientX - startX;
          if (!moved && Math.abs(dx) > 5) {
            moved = true;
            grid.classList.add('is-dragging');
          }
          if (moved) {
            e.preventDefault();
            grid.scrollLeft = startScroll - dx;
          }
        });

        const end = () => {
          if (!isDown) return;
          isDown = false;
          if (moved) {
            grid.classList.remove('is-dragging');
            // Snap to the nearest card after letting go
            const s = step();
            grid.scrollTo({ left: Math.round(grid.scrollLeft / s) * s, behavior: 'smooth' });
          }
        };
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);

        // Don't open a product when the click was really a drag
        grid.addEventListener('click', (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        }, true);
        grid.addEventListener('dragstart', (e) => e.preventDefault());

        update();
        setTimeout(update, 400);
      }

      async fetchFor(productId) {
        const url =
          this.dataset.url +
          '?section_id=' + encodeURIComponent(this.dataset.sectionId) +
          '&product_id=' + encodeURIComponent(productId) +
          '&limit=10&intent=related';
        const res = await fetch(url);
        if (!res.ok) return [];
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('[data-cart-recs-results] .cart-recs__item'));
      }
    }
  );
}

/* ---------- Cart drawer loader ---------- */
if (!window.__cartDrawerRecsBound) {
  window.__cartDrawerRecsBound = true;

  const isDrawerOpen = (drawer) =>
    drawer && !drawer.hasAttribute('hidden') && drawer.getClientRects().length > 0;

  const loadDrawerRecs = async (container) => {
    container.dataset.state = 'loading';
    try {
      const res = await fetch(container.dataset.sectionUrl);
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const section = doc.querySelector('.shopify-section') || doc.body;

      // Drawer settings override the section defaults
      const recs = section.querySelector('cart-recommendations');
      if (recs && container.dataset.limit) recs.setAttribute('data-limit', container.dataset.limit);
      const heading = section.querySelector('.cart-recs__heading');
      if (heading && container.dataset.heading) heading.textContent = container.dataset.heading;

      section.querySelectorAll('script').forEach((s) => s.remove());
      // Only replace if the theme hasn't swapped this container out meanwhile
      if (container.isConnected) {
        container.replaceChildren(...Array.from(section.childNodes).map((n) => document.importNode(n, true)));
      }
      container.dataset.state = 'loaded';
    } catch (e) {
      container.dataset.state = 'error';
    }
  };

  // A fingerprint of what's in the drawer, so we reload when the cart changes
  const cartSignature = (drawer) =>
    Array.from(drawer.querySelectorAll('cart-items .cart-item'))
      .map((li) => (li.dataset.variantId || li.id) + ':' + (li.dataset.quantity || ''))
      .join('|');

  const check = () => {
    document.querySelectorAll('[data-cart-drawer-recs]').forEach((container) => {
      const drawer = container.closest('cart-drawer');
      if (!isDrawerOpen(drawer) || container.dataset.state === 'loading') return;
      const sig = cartSignature(drawer);
      if (!sig) return;
      if (!container.dataset.state || container.dataset.sig !== sig) {
        container.dataset.sig = sig;
        loadDrawerRecs(container);
      }
    });
  };

  let queued = false;
  const queueCheck = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      check();
    });
  };

  const start = () => {
    new MutationObserver(queueCheck).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'open', 'class', 'aria-hidden'],
    });
    queueCheck();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}